// The owner's TUNE account: who they are (their name, as people they share with see it), signing in (a new password,
// staying signed in, signing out here or everywhere), every TUNE MFD and other device signed in (moved, or signed out),
// what the account holds against its limits, what comes to it and when, and deleting it.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@tune/shared';
import { useId, useState, type FormEvent } from 'react';
import { ApiError, data, problem } from '../../api';
import { useAuth } from '../../auth';
import { authProblem } from '../../auth/errors';
import { size } from '../../items';
import { dayText, deviceName, isMfd } from '../../lib/words';
import { keys, useAccount, useDevices, useVehicles } from '../../queries';
import { Page } from '../../shell/Page';
import { useSignOut, whatSigningOutLoses } from './sign-out';
import {
  Avatar,
  Badge,
  Banner,
  Button,
  Disclosure,
  ErrorBanner,
  ListButton,
  ListControl,
  ListLink,
  ListSection,
  ListValue,
  Meter,
  SecureField,
  Sheet,
  Skeleton,
  Switch,
  TextField,
  useConfirm,
  useRouteSheet,
  useToast,
  type Rule,
} from '../../ui';
import { KeyIcon, PencilSimpleIcon, QuestionIcon, SignOutIcon, TrashIcon, GaugeIcon } from '../../ui/icons';
import { ChooseCarSheet, SeenLine, useSignOutDevice } from '../garage/Mfd';
import s from './Account.module.css';

type Account = components['schemas']['Account'];
type Device = components['schemas']['Device'];

/** The least an owner's password can be: the owners' pool's rule (auth/pages/parts.ts). */
const LEAST = 10;

export function AccountPage() {
  const account = useAccount();
  return (
    <Page title="Account">
      {account.isError && <ErrorBanner retry={() => void account.refetch()}>Your account couldn't load.</ErrorBanner>}
      {account.isPending && <Skeleton blocks={3} lines={0} label="Loading your account" />}
      {account.data && <AccountOf account={account.data} />}
    </Page>
  );
}

function AccountOf({ account }: { account: Account }) {
  const [naming, setNaming] = useRouteSheet('name');
  const [changing, setChanging] = useRouteSheet('password');
  const [deleting, setDeleting] = useRouteSheet('delete');
  const admin = account.role === 'admin';
  return (
    <>
      <section className={s.profile} aria-label="You">
        <Avatar name={account.displayName} email={account.email} size="large" />
        <div className={s.who}>
          <p className={s.name}>
            {account.displayName || account.email}
            {admin && <Badge tone="accent">Admin</Badge>}
          </p>
          <p className={s.meta}>
            {account.email} · since {dayText(account.createdAt)}
          </p>
        </div>
        <Button
          variant="gray"
          size="small"
          icon={<PencilSimpleIcon size={16} weight="bold" />}
          onClick={() => setNaming(true)}
        >
          Edit name
        </Button>
      </section>
      <div className={s.columns}>
        <div className={s.column}>
          <SigningIn onPassword={() => setChanging(true)} />
          <Devices />
          {admin && (
            <ListSection
              header="For TUNE"
              footer="You invite people, and make groups whose members get what TUNE gives them."
            >
              <ListLink to="/admin" icon={<GaugeIcon size={20} weight="bold" />} label="Admin dashboard" />
            </ListSection>
          )}
        </div>
        <div className={s.column}>
          <Holds account={account} />
          <Comes />
          <ListSection>
            <ListLink to="/help" icon={<QuestionIcon size={20} weight="bold" />} label="Help" />
          </ListSection>
          <ListSection footer="Your cars, library, shares and recordings go for good, and every device is signed out.">
            <ListButton
              tone="destructive"
              icon={<TrashIcon size={20} weight="bold" />}
              label="Delete your account"
              onClick={() => setDeleting(true)}
            />
          </ListSection>
        </div>
      </div>
      <NameSheet account={account} open={naming} onClose={() => setNaming(false)} />
      <PasswordSheet open={changing} onClose={() => setChanging(false)} />
      <DeleteSheet account={account} open={deleting} onClose={() => setDeleting(false)} />
    </>
  );
}

/** Signing in: a new password, staying signed in in this browser, and signing out here or everywhere. */
function SigningIn({ onPassword }: { onPassword: () => void }) {
  const auth = useAuth();
  const confirm = useConfirm();
  const signOut = useSignOut();
  const session = auth.session('owners');
  const everywhere = async () => {
    const loses = await whatSigningOutLoses();
    const yes = await confirm({
      title: 'Sign out everywhere?',
      message: `Every browser signed in to your TUNE account is signed out, this one too. Your TUNE MFDs stay signed in: sign one out below.${loses ? ` ${loses}` : ''}`,
      confirm: 'Sign out everywhere',
    });
    if (yes) {
      await auth.signOutEverywhere('owners');
    }
  };
  return (
    <ListSection header="Signing in">
      <ListButton icon={<KeyIcon size={20} weight="bold" />} label="Change your password" onClick={onPassword} />
      <ListControl
        label="Keep me signed in"
        subtitle={session?.keep === 'browser' ? 'In this browser, until you sign out.' : 'Only until this tab closes.'}
        control={
          <Switch
            checked={session?.keep === 'browser'}
            onChange={(e) => auth.keep('owners', e.target.checked ? 'browser' : 'tab')}
          />
        }
      />
      <ListButton
        icon={<SignOutIcon size={20} weight="bold" />}
        label="Sign out"
        disabled={auth.signingOut('owners')}
        onClick={() => void signOut()}
      />
      <ListButton tone="destructive" label="Sign out everywhere" onClick={() => void everywhere()} />
    </ListSection>
  );
}

/** Every TUNE MFD, then any other device, signed in to the account. */
function Devices() {
  const devices = useDevices();
  const vehicles = useVehicles();
  if (devices.isPending || vehicles.isPending) {
    return <Skeleton lines={3} label="Loading what's signed in" />;
  }
  if (devices.isError || vehicles.isError) {
    return (
      <ErrorBanner retry={() => void Promise.all([devices.refetch(), vehicles.refetch()])}>
        What's signed in couldn't load.
      </ErrorBanner>
    );
  }
  const cars = vehicles.data.vehicles;
  const mfds = devices.data.devices.filter(isMfd);
  const others = devices.data.devices.filter((d) => !isMfd(d));
  const carOf = (d: Device) => cars.find((c) => c.id === d.vehicleId) ?? null;
  return (
    <>
      <ListSection
        header="TUNE MFDs"
        footer="Signed out, a TUNE MFD stops syncing until it's signed in again with the code on its TUNE account page."
      >
        {mfds.length === 0 && <ListValue label="None signed in yet" subtitle="Sign one in from Home, or the garage." />}
        {mfds.map((d) => (
          <DeviceRow key={d.id} device={d} car={carOf(d)} />
        ))}
      </ListSection>
      {others.length > 0 && (
        <ListSection header="Other sign-ins" footer="Other devices signed in to your account, each by its own name.">
          {others.map((d) => (
            <DeviceRow key={d.id} device={d} car={carOf(d)} />
          ))}
        </ListSection>
      )}
    </>
  );
}

function DeviceRow({ device, car }: { device: Device; car: components['schemas']['Vehicle'] | null }) {
  const signOut = useSignOutDevice(device);
  const [moving, setMoving] = useRouteSheet(`move-${device.id}`);
  const name = deviceName(device);
  return (
    <div className={s.device}>
      <div className={s.deviceText}>
        <span className={s.deviceName}>{name}</span>
        <span className={s.meta}>
          {car ? `In ${car.nickname}` : 'In no car'} · <SeenLine device={device} />
          {isMfd(device) && device.appVersion ? ` · TUNE Live ${device.appVersion}` : ''}
        </span>
      </div>
      <div className={s.deviceActions}>
        {isMfd(device) && (
          <Button variant="plain" size="small" onClick={() => setMoving(true)}>
            {car ? 'Move' : 'Put in a car'}
            <span className="visually-hidden"> {name}</span>
          </Button>
        )}
        <Button variant="destructivePlain" size="small" busy={signOut.pending} onClick={() => void signOut.ask()}>
          Sign out<span className="visually-hidden"> {name}</span>
        </Button>
      </div>
      {isMfd(device) && <ChooseCarSheet device={device} from={car} open={moving} onClose={() => setMoving(false)} />}
    </div>
  );
}

/** What the account holds against its limits. */
function Holds({ account }: { account: Account }) {
  const u = account.usage;
  const q = account.quota;
  return (
    <ListSection header="What it holds" footer="Recordings are kept for 12 months.">
      <div className={s.meters}>
        <Meter label="Cars" value={u.vehicles} max={q.vehicles} valueText={`${u.vehicles} of ${q.vehicles}`} />
        <Meter
          label="Your library"
          value={u.libraryBytes}
          max={q.libraryBytes}
          valueText={`${u.libraryItems} of ${q.libraryItems} items, ${size(u.libraryBytes)} of ${size(q.libraryBytes)}`}
        />
        <Meter
          label="Recordings"
          value={u.recordingsBytes}
          max={q.recordingsBytes}
          valueText={`${size(u.recordingsBytes)} of ${size(q.recordingsBytes)}`}
        />
      </div>
    </ListSection>
  );
}

/** What comes to the account, and when: as the MFD sends it. */
function Comes() {
  return (
    <section className={s.comes} aria-labelledby="comes">
      <h2 id="comes" className={s.comesTitle}>
        What comes here, and when
      </h2>
      <Disclosure summary="Your dashboards and configs">
        Your MFDs send the ones you make or change on them, and every MFD of yours gets them. So do those from TUNE's
        theme editor, and those you add here.
      </Disclosure>
      <Disclosure summary="Each car's settings, connections and trips">
        From its MFD, and back to it: a change here reaches the MFD at its next sync, once the car is stopped.
      </Disclosure>
      <Disclosure summary="Datalogs, track days and trips">
        From each MFD while it's signed in, when the car is stopped. Datalogs show where the car went. Each is kept for
        12 months.
      </Disclosure>
      <Disclosure summary="Diagnostics">
        Only when you choose to send them to TUNE, from the MFD's Log page. TUNE keeps them for 90 days.
      </Disclosure>
    </section>
  );
}

/** Your name, as people you share with see it. */
function NameSheet({ account, open, onClose }: { account: Account; open: boolean; onClose: () => void }) {
  const id = useId();
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const say = useToast();
  const save = useMutation({
    mutationFn: (displayName: string) => data(api.PATCH('/account', { body: { displayName } })),
    onSuccess: (saved) => {
      queryClient.setQueryData(keys.account, saved);
      onClose();
      say('Saved.');
    },
  });
  return (
    <Sheet
      open={open}
      onClose={() => {
        save.reset();
        onClose();
      }}
      title="Your name"
      action={{ label: 'Save', form: id, busy: save.isPending }}
    >
      <NameForm id={id} start={account.displayName} failed={save.error} onSave={(name) => save.mutate(name)} />
    </Sheet>
  );
}

function NameForm({
  id,
  start,
  failed,
  onSave,
}: {
  id: string;
  start: string;
  failed: unknown;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(start);
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give a name, like Sam Lee.');
      return;
    }
    onSave(name.trim());
  };
  return (
    <form id={id} className={s.form} onSubmit={submit} noValidate>
      {failed !== null && failed !== undefined && <Banner tone="error">{problem(failed)}</Banner>}
      <TextField
        label="Name"
        autoComplete="name"
        maxLength={60}
        hint={`People you share with see it, as "Shared by ${name.trim() || 'you'}".`}
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setError(null);
        }}
        error={error}
        data-autofocus
      />
    </form>
  );
}

/** A new password: the one now, then the new one with the rules ticking off as it's typed. */
function PasswordSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const id = useId();
  const auth = useAuth();
  const say = useToast();
  const change = useMutation({
    mutationFn: ({ previous, proposed }: { previous: string; proposed: string }) =>
      auth.changePassword('owners', previous, proposed),
    onSuccess: () => {
      onClose();
      say('Your password is changed. Other browsers stay signed in until you sign out everywhere.');
    },
  });
  return (
    <Sheet
      open={open}
      onClose={() => {
        change.reset();
        onClose();
      }}
      title="Change your password"
      action={{ label: 'Change', form: id, busy: change.isPending }}
    >
      <PasswordForm
        id={id}
        failed={change.error}
        onChange={(previous, proposed) => change.mutate({ previous, proposed })}
      />
    </Sheet>
  );
}

function PasswordForm({
  id,
  failed,
  onChange,
}: {
  id: string;
  failed: unknown;
  onChange: (previous: string, proposed: string) => void;
}) {
  const [previous, setPrevious] = useState('');
  const [proposed, setProposed] = useState('');
  const [problemText, setProblemText] = useState<string | null>(null);
  const rules: Rule[] = [
    { label: `At least ${LEAST} characters`, met: proposed.length >= LEAST },
    { label: 'Not the one you have now', met: proposed.length > 0 && proposed !== previous },
  ];
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!previous) {
      setProblemText('Give the password you sign in with now.');
      return;
    }
    if (!rules.every((r) => r.met)) {
      setProblemText(`Use a new password of at least ${LEAST} characters.`);
      return;
    }
    setProblemText(null);
    onChange(previous, proposed);
  };
  const said = problemText ?? (failed ? authProblem(failed, 'changePassword', LEAST) : null);
  return (
    <form id={id} className={s.form} onSubmit={submit} noValidate>
      {said && <Banner tone="error">{said}</Banner>}
      <SecureField
        label="Your password now"
        autoComplete="current-password"
        value={previous}
        onChange={(e) => setPrevious(e.target.value)}
        data-autofocus
      />
      <SecureField
        label="New password"
        autoComplete="new-password"
        value={proposed}
        onChange={(e) => setProposed(e.target.value)}
        rules={rules}
      />
    </form>
  );
}

/** Deleting the account, after typing its email: it can't be undone. */
function DeleteSheet({ account, open, onClose }: { account: Account; open: boolean; onClose: () => void }) {
  const auth = useAuth();
  const api = auth.api('owners');
  const [typed, setTyped] = useState('');
  const remove = useMutation({
    mutationFn: () => data(api.POST('/account/delete', { body: { email: account.email } })),
    onSuccess: () => void auth.signOut('owners'),
  });
  const matches = typed.trim().toLowerCase() === account.email.toLowerCase();
  return (
    <Sheet
      open={open}
      onClose={() => {
        setTyped('');
        remove.reset();
        onClose();
      }}
      title="Delete your account"
      footer={
        <Button variant="destructive" wide disabled={!matches} busy={remove.isPending} onClick={() => remove.mutate()}>
          Delete my account
        </Button>
      }
    >
      <p className={s.warning}>
        Every device is signed out, and your cars, library, shares and recordings go for good. It can't be undone. TUNE
        emails you when it's done.
      </p>
      {remove.isError && (
        <Banner tone="error">
          {remove.error instanceof ApiError && remove.error.status === 400
            ? "That isn't your account's email."
            : problem(remove.error)}
        </Banner>
      )}
      <TextField
        label="To delete it, type your email"
        type="email"
        autoComplete="off"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
      />
    </Sheet>
  );
}
